// Detalle público de un partido abierto ("Falta un jugador"). Rutas (ver src/router/routes.js):
//   /partidos/:id                      -> GET /api/app/open-matches/:id
//   /partidos/compartir/:shareToken    -> GET /api/app/open-matches/share/:shareToken (link para compartir)
// Pasos internos: "detail" (resumen + reglas) -> "datos" (OpenMatchJoinForm) -> "success" (¡Estás adentro!).
// Estados: open, confirmed, cancelled, kept y closed (open pasada su hora de cierre, ver matchViewState);
// solo "open" permite sumarse. v1 no cobra online: cada jugador paga su parte en el club.
//
// Vista del jugador (T11): con `?token=<manageToken>` (o el token guardado al sumarse/crear) se llama a
// GET /:id/me y se muestra el panel "Tu lugar" (OpenMatchPlayerPanel): bajarse (jugador) o cancelar (creador),
// ambos con confirmación en línea. El token viaja solo en el header x-manage-token, nunca se loguea ni se
// renderiza. Si el back lo rechaza (401/403) se borra el guardado y se muestra la vista pública con un aviso;
// otros errores (red, 5xx) dejan la vista pública sin tocar el token para poder reintentar.
//
// Sin token de gestión pero con sesión iniciada (T15), /me, /leave y /cancel van con el bearer del jugador:
// el back lo resuelve a su lugar en el partido. Ahí un 403 solo significa "no sos jugador": sin panel y sin
// aviso, y no se borra nada guardado.
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
	getOpenMatchByIdApi,
	getOpenMatchByShareTokenApi,
	getOpenMatchMeApi,
	joinOpenMatchApi,
	leaveOpenMatchApi,
	cancelOpenMatchApi,
} from "../../api/api";
import { SPORT_LABELS } from "../../constants/courtDomain";
import RIcon from "../reservas/components/ReservasIcons";
import ShareMatchButton from "./components/ShareMatchButton";
import OpenMatchJoinForm from "./components/OpenMatchJoinForm";
import OpenMatchJoinSuccess from "./components/OpenMatchJoinSuccess";
import OpenMatchPlayerPanel from "./components/OpenMatchPlayerPanel";
import {
	MATCH_KIND_LABELS,
	MATCH_STATE_LABELS,
	buildJoinPayload,
	clearManageToken,
	formatCloseAt,
	formatMatchDay,
	formatPricePerPlayer,
	isManageTokenRejected,
	joinErrorMessage,
	manageActionErrorMessage,
	matchViewState,
	missingLabel,
	playerInitials,
	readManageToken,
	saveManageToken,
	shareUrl,
} from "./openMatchesModel";
import "../reservas/reservas.css";
import "./components/OpenMatchCard.css";

const STATE_NOTES = {
	confirmed: "El partido está completo y la cancha quedó reservada.",
	cancelled: "Este partido fue cancelado. Mirá otros partidos abiertos del club.",
	kept: "No se completó, pero quien lo creó se quedó con la cancha. Ya no se puede sumar nadie.",
	closed: "La hora de cierre ya pasó: no se aceptan más jugadores.",
};

// Trailing args for the manage APIs: [manageToken] or, with a session, [manageToken || "", bearer].
const authArgs = (manageToken, authToken) => (authToken ? [manageToken || "", authToken] : [manageToken]);

const OpenMatchDetail = () => {
	const { id, shareToken } = useParams();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const authUser = useSelector((s) => s.auth?.user);
	const authToken = useSelector((s) => s.auth?.accessToken);

	const [match, setMatch] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [step, setStep] = useState("detail");
	const [submitting, setSubmitting] = useState(false);
	const [joinError, setJoinError] = useState("");
	const [joined, setJoined] = useState(null);

	// Token de gestión del jugador (link del aviso o guardado al sumarse). Se descarta si el back lo rechaza.
	const [manageToken, setManageToken] = useState(() => (id ? searchParams.get("token") || readManageToken(id) : ""));
	const [me, setMe] = useState(null);
	// True once the /me lookup settled; until then we don't know if the visitor already plays here.
	const [meSettled, setMeSettled] = useState(false);
	const [tokenRejected, setTokenRejected] = useState(false);
	// "left" | "cancelled" tras una acción exitosa; define el texto del panel.
	const [outcome, setOutcome] = useState(null);
	const [acting, setActing] = useState(false);
	const [manageError, setManageError] = useState("");

	useEffect(() => {
		let active = true;
		(async () => {
			try {
				setLoading(true);
				setError("");
				const res = shareToken ? await getOpenMatchByShareTokenApi(shareToken) : await getOpenMatchByIdApi(id);
				if (active) setMatch(res?.data?.info?.match || null);
			} catch (err) {
				if (active) {
					setMatch(null);
					setError(
						err?.response?.status === 404 || err?.response?.status === 400
							? "No encontramos este partido."
							: err?.response?.data?.message || "No pudimos cargar el partido.",
					);
				}
			} finally {
				if (active) setLoading(false);
			}
		})();
		return () => {
			active = false;
		};
	}, [id, shareToken]);

	// Vista del jugador: solo en la ruta por id y mientras haya token. Un rechazo (401/403) lo descarta.
	useEffect(() => {
		if (!id || (!manageToken && !authToken)) return undefined;
		let active = true;
		setMeSettled(false);
		(async () => {
			try {
				const res = await getOpenMatchMeApi(id, ...authArgs(manageToken, authToken));
				if (!active) return;
				const info = res?.data?.info || {};
				if (info.me) {
					setMe(info.me);
					if (info.match) setMatch(info.match);
					if (manageToken) saveManageToken(id, manageToken);
				}
			} catch (err) {
				// Bearer-only: a rejection just means "not a player", nothing to clear.
					if (active && manageToken && isManageTokenRejected(err)) {
					clearManageToken(id);
					setManageToken("");
					setMe(null);
					setTokenRejected(true);
				}
			} finally {
				if (active) setMeSettled(true);
			}
		})();
		return () => {
			active = false;
		};
	}, [id, manageToken, authToken]);

	// Identity (manage token or session) is being resolved: don't offer "Sumarme" to someone who may already play.
	const checkingPlayer = Boolean(id) && Boolean(manageToken || authToken) && !meSettled;
	const view = useMemo(() => matchViewState(match), [match]);
	const players = Array.isArray(match?.players) ? match.players : [];
	const emptySlots = Math.max(0, (Number(match?.capacity) || 0) - players.length);
	// El link para compartir usa el token público si entramos por él; si no, la URL por id (los partidos públicos se leen por id).
	const shareLink = shareToken ? shareUrl(shareToken) : `${window.location.origin}/partidos/${match?._id || id}`;

	const handleJoin = async (values, country, marketingOptIn) => {
		try {
			setSubmitting(true);
			setJoinError("");
			const res = await joinOpenMatchApi(match._id, buildJoinPayload(values, country, marketingOptIn));
			const info = res?.data?.info || {};
			saveManageToken(match._id, info.manageToken);
			setJoined({ match: info.match || match, manageToken: info.manageToken || "" });
			setStep("success");
		} catch (err) {
			setJoinError(joinErrorMessage(err));
		} finally {
			setSubmitting(false);
		}
	};

	// Bajarse: el back devuelve el partido actualizado; el token deja de servir (el jugador ya no está).
	const handleLeave = async () => {
		try {
			setActing(true);
			setManageError("");
			const res = await leaveOpenMatchApi(match._id, ...authArgs(manageToken, authToken));
			if (res?.data?.info?.match) setMatch(res.data.info.match);
			clearManageToken(match._id);
			setMe((m) => ({ ...m, left: true, canLeave: false, canCancel: false }));
			setOutcome("left");
			return true;
		} catch (err) {
			setManageError(manageActionErrorMessage(err, "No pudimos bajarte del partido. Probá de nuevo."));
			return false;
		} finally {
			setActing(false);
		}
	};

	// Cancelar (solo el creador): libera la cancha y avisa a los jugadores. Idempotente en el back.
	const handleCancel = async () => {
		try {
			setActing(true);
			setManageError("");
			const res = await cancelOpenMatchApi(match._id, ...authArgs(manageToken, authToken));
			if (res?.data?.info?.match) setMatch(res.data.info.match);
			setMe((m) => ({ ...m, canLeave: false, canCancel: false }));
			setOutcome("cancelled");
			return true;
		} catch (err) {
			setManageError(manageActionErrorMessage(err, "No pudimos cancelar el partido. Probá de nuevo."));
			return false;
		} finally {
			setActing(false);
		}
	};

	// Back: one entry if the router has history (react-router stores `idx`), else the open matches list.
	const goBack = () => {
		if (window.history.state?.idx > 0) navigate(-1);
		else navigate("/canchas?vista=partidos");
	};

	if (loading) {
		return (
			<div className="reservas-root">
				<div className="wrap" style={{ textAlign: "center", padding: "80px 0" }}>
					<Spinner animation="border" />
				</div>
			</div>
		);
	}

	if (!match) {
		return (
			<div className="reservas-root">
				<div className="wrap">
					<Alert variant="danger">{error || "No encontramos este partido."}</Alert>
					<button type="button" className="r-btn-soft" onClick={() => navigate("/canchas")}>
						Ver partidos abiertos
					</button>
				</div>
			</div>
		);
	}

	if (step === "success" && joined) {
		return (
			<div className="reservas-root">
				<div className="wrap">
					<OpenMatchJoinSuccess match={joined.match} manageToken={joined.manageToken} shareLink={shareLink} />
				</div>
			</div>
		);
	}

	if (step === "datos") {
		return (
			<div className="reservas-root">
				<div className="wrap open-match-detail">
					<OpenMatchJoinForm
						clubName={match.clubName}
						defaults={authUser || {}}
						submitting={submitting}
						error={joinError}
						onSubmit={handleJoin}
						onCancel={() => setStep("detail")}
					/>
				</div>
			</div>
		);
	}

	const facts = [
		{ icon: "ticket", l: "Cancha", v: match.courtName },
		{ icon: "ball", l: "Deporte", v: SPORT_LABELS[match.sport] || match.sport },
		{ icon: "cal", l: "Día", v: formatMatchDay(match.date) },
		{ icon: "clock", l: "Horario", v: `${match.slotStart} - ${match.slotEnd}` },
		{ icon: "clock", l: "Duración", v: `${match.durationMinutes} min` },
		{ icon: "users", l: "Tipo", v: MATCH_KIND_LABELS[match.kind] || match.kind },
	];
	const showJoin = view === "open" && !me && !checkingPlayer;

	// Layout (T21): main column + sticky aside card (like the booking `.form-card` + `.sum-card`). Below 1080px it is a
	// single column and `.omd-cta` (the "Sumarme" button) is pinned to the bottom, same look as `.rmob-cta-bar`.
	return (
		<div className="reservas-root">
			<div className="wrap omd-page">
				<button type="button" className="crumb" onClick={goBack}>
					<RIcon name="chevL" width={16} height={16} /> Volver
				</button>
				<div className="omd-grid">
					<div className="form-card omd-main" data-testid="open-match-main">
						<div className="omd-head">
							<div>
								<h1 className="bar-name" style={{ fontSize: 26 }}>{match.clubName}</h1>
								{match.clubAddress && (
									<div className="bar-loc">
										<RIcon name="pin" width={14} height={14} />
										{match.clubAddress}
									</div>
								)}
							</div>
							<span className={`open-match-pill is-${view}`}>{MATCH_STATE_LABELS[view]}</span>
						</div>

						{STATE_NOTES[view] && <Alert variant={view === "cancelled" ? "danger" : "secondary"}>{STATE_NOTES[view]}</Alert>}

						<div className="omd-facts">
							{facts.map((f) => (
								<div className="omd-fact" key={f.l}>
									<RIcon name={f.icon} />
									<div><div className="l">{f.l}</div><div className="v">{f.v}</div></div>
								</div>
							))}
						</div>

						<div className="open-match-slots">
							{players.map((p) => (
								<span key={p._id} className="open-match-avatar" data-testid="open-match-slot-filled" title={p.name}>
									{playerInitials(p.name)}
								</span>
							))}
							{Array.from({ length: emptySlots }).map((_, i) => (
								<span key={`empty-${i}`} className="open-match-avatar is-empty" data-testid="open-match-slot-empty">+</span>
							))}
							{view === "open" && <span className="open-match-missing">{missingLabel(match.missing)}</span>}
						</div>

						<div className="open-match-rules">
							<h3>Cómo funciona</h3>
							<ul>
								<li>Cuando se completa la cancha, queda reservada para el grupo.</li>
								<li>
									{match.ifNotFull === "keep"
										? "Si a la hora de cierre no se completa, quien lo creó se queda con la cancha y el partido sigue."
										: "Si a la hora de cierre no se completa, el partido se cancela y la cancha se libera."}
								</li>
								<li>Podés bajarte del partido hasta la hora de cierre.</li>
							</ul>
						</div>
					</div>

					<aside className="sum-card omd-aside" data-testid="open-match-aside">
						<div className="sum-body">
							{tokenRejected && !me && (
								<Alert variant="warning">Tu link de gestión ya no es válido. Igual podés ver el partido.</Alert>
							)}
							{me && (
								<OpenMatchPlayerPanel
									me={me}
									outcome={outcome}
									canRejoin={view === "open"}
									busy={acting}
									error={manageError}
									onLeave={handleLeave}
									onCancel={handleCancel}
									onRejoin={() => setStep("datos")}
								/>
							)}
							<div className="omd-price">
								<b>{formatPricePerPlayer(match.pricePerPlayer)}</b> <span>por jugador</span>
							</div>
							<p className="omd-note">Pagás tu parte en el club: no hay pago online.</p>
							{match.closeAt && view === "open" && <p className="open-match-close">{formatCloseAt(match.closeAt)}</p>}
							{showJoin && (
								<div className="omd-cta">
									<button type="button" className="r-btn-primary" onClick={() => setStep("datos")}>
										Sumarme
									</button>
								</div>
							)}
							<ShareMatchButton url={shareLink} className="r-btn-soft omd-share" />
						</div>
					</aside>
				</div>
			</div>
		</div>
	);
};

export default OpenMatchDetail;
